import { useState } from "react";
import { Layout } from "../components/Layout";
import { Breadcrumb } from "../components/Topbar";
import { PageHeader } from "../components/PageHeader";
import { Kpi } from "../components/Kpi";
import { useTenants } from "../hooks/useTenants";
import { useBillingSnapshots, type BillingSnapshot } from "../hooks/useBillingSnapshots";
import { useMrrSummary } from "../hooks/useMrrSummary";
import { MrrChart } from "../components/billing/MrrChart";
import { InvoicesTable } from "../components/billing/InvoicesTable";
import { GenerateInvoiceModal } from "../components/billing/GenerateInvoiceModal";
import { ConfirmSendChargeDialog } from "../components/billing/ConfirmSendChargeDialog";
import { supabase } from "../lib/supabase";
import { formatBRL } from "../lib/format";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";

export function BillingPage() {
  const { tenants } = useTenants();
  const { snapshots, loading, refetch } = useBillingSnapshots({ limit: 200 });
  const { data: summary } = useMrrSummary();
  const [showModal, setShowModal] = useState(false);
  const [sendingSnapshot, setSendingSnapshot] = useState<BillingSnapshot | null>(null);

  const onAction = async (action: string, s: BillingSnapshot) => {
    switch (action) {
      case "send": {
        setSendingSnapshot(s);
        return;
      }
      case "mark_paid": {
        if (!confirm("Marcar como paga?")) return;
        await supabase.functions.invoke("platform-mark-snapshot-paid", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "void": {
        if (!confirm("Anular esta fatura? (não cancela no Asaas)")) return;
        await supabase.functions.invoke("platform-void-snapshot", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "regenerate": {
        if (!confirm("Regenerar? (deleta + recria com counts atuais)")) return;
        await supabase.functions.invoke("platform-regenerate-snapshot", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "cancel": {
        if (!confirm("Cancelar cobrança no Asaas + anular?")) return;
        await supabase.functions.invoke("platform-cancel-charge", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "refund": {
        if (!confirm("Reembolsar fatura paga?")) return;
        await supabase.functions.invoke("platform-refund-charge", { body: { snapshotId: s.id } });
        refetch();
        return;
      }
      case "view": {
        const t = tenants.find((tt) => tt.id === s.tenant_id);
        if (t) window.location.href = `/tenants/${t.slug}/billing`;
        return;
      }
    }
  };

  const currentMrr = summary?.current_mrr ?? 0;
  const deltaPct = summary?.delta_pct ?? null;
  const now = new Date();
  const paidThisMonth = snapshots.filter(
    (s) =>
      s.status === "paid" &&
      s.paid_at &&
      new Date(s.paid_at).getMonth() === now.getMonth() &&
      new Date(s.paid_at).getFullYear() === now.getFullYear(),
  ).length;
  const overdueCount = snapshots.filter((s) => s.status === "overdue").length;
  const receivable = snapshots
    .filter((s) => ["sent", "overdue"].includes(s.status))
    .reduce((sum, s) => sum + Number(s.final_total), 0);

  const mrrDelta =
    deltaPct !== null
      ? {
          direction: (deltaPct > 0 ? "up" : deltaPct < 0 ? "down" : "flat") as "up" | "down" | "flat",
          text: `${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}% vs mês -1`,
        }
      : undefined;

  const generateBtn: React.CSSProperties = {
    height: 36,
    padding: `0 ${space.md + 2}px`,
    background: colors.indigo,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    cursor: "pointer",
  };

  return (
    <Layout breadcrumbs={<Breadcrumb items={[{ label: "Faturamento" }]} />}>
      <PageHeader
        title="Faturamento"
        subtitle="Visão consolidada cross-tenant"
        actions={
          <button onClick={() => setShowModal(true)} style={generateBtn}>
            + Gerar fatura
          </button>
        }
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: space.lg,
          marginBottom: space.xxxl,
        }}
      >
        <Kpi
          label="MRR"
          value={formatBRL(currentMrr)}
          delta={mrrDelta}
          sub={deltaPct === null ? "sem histórico anterior" : undefined}
        />
        <Kpi label="Pago / mês" value={String(paidThisMonth)} />
        <Kpi label="Atrasadas" value={String(overdueCount)} />
        <Kpi label="A receber" value={formatBRL(receivable)} />
      </div>

      <div style={{ marginBottom: space.lg }}>
        <MrrChart />
      </div>

      <InvoicesTable snapshots={snapshots} tenants={tenants} loading={loading} onAction={onAction} />

      {showModal && (
        <GenerateInvoiceModal
          tenants={tenants}
          onClose={() => setShowModal(false)}
          onCreated={() => refetch()}
        />
      )}
      {sendingSnapshot && (
        <ConfirmSendChargeDialog
          snapshot={sendingSnapshot}
          onClose={() => setSendingSnapshot(null)}
          onSent={() => refetch()}
        />
      )}
    </Layout>
  );
}
